import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type CSSProperties, useEffect, useMemo, useState } from 'react'
import { Avatar } from '@/features/auth/Avatar'
import type { UserCard } from '@/features/auth/types'
import { BoardView } from '@/features/board/BoardView'
import { faceTo } from '@/features/board/layout'
import { useBoard } from '@/features/board/useBoard'
import { useBoardZoom } from '@/features/board/useBoardZoom'
import { ChatBox } from '@/features/chat/ChatBox'
import { ChatDock } from '@/features/chat/ChatDock'
import { hasChat } from '@/features/chat/rules'
import { useChatOn } from '@/features/chat/settings'
import { reveal, useChat } from '@/features/chat/useChat'
import { useSpeech } from '@/features/chat/useSpeech'
import { FriendInvite } from '@/features/friends/FriendInvite'
import { GearIcon, HelpIcon } from '@/features/lobby/icons'
import { loadPrefs, type Prefs } from '@/features/lobby/prefs'
import { startWithBots } from '@/features/lobby/start'
import { useLocalBot } from '@/features/local/useLocalBot'
import type { BoardGuide, BoardPerson, Cell, PlacedToken, PlayerColor } from '@/shared/board/types'
import {
  addBot,
  busyWith,
  isGone,
  joinGame,
  leaveGame,
  removeBot,
  rollDice,
  sendMove,
  setReady,
} from '@/shared/game/api'
import { forgetGame, markOver, savedName } from '@/shared/game/storage'
import type { BotLevel, GameView, MoveStep } from '@/shared/game/types'
import { createLocalGame } from '@/shared/local/store'
import { CentreDice } from './CentreDice'
import { lastRoll, usedFlags } from './Dice'
import { MoveChips } from './MoveChips'
import { MiniMap, PhonePlay, PlayersBar } from './PhoneTable'
import { type MoveOption, optionsOf, placeTokens } from './placement'
import { useCueOnChange, warmSounds } from './sound'
import { TokenRail } from './TokenRail'
import { useBonusCallouts } from './useBonusCallouts'
import { useBotWatchdog } from './useBotWatchdog'
import { useConnection } from './useConnection'
import { gameKey, useGame } from './useGame'
import { useLobbyPresence } from './useLobbyPresence'
import { CameraAim, usePhoneCamera } from './usePhoneCamera'
import { usePlayback } from './usePlayback'

/** Названия цветов в родительном падеже: «вы играете за красных». */
const COLOUR_NAME: Record<string, string> = {
  red: 'красных',
  green: 'зелёных',
  yellow: 'жёлтых',
  blue: 'синих',
}

/** Цвет игрока как переменная — ею подкрашиваются рамки и заголовки. */
function own(colour: string | null): CSSProperties {
  return { '--own': colour === null ? 'var(--muted)' : `var(--${colour})` } as CSSProperties
}

/** Уровни компьютера — App\Domain\Ludo\Bot\BotLevel. */
const LEVELS: { value: BotLevel; label: string }[] = [
  { value: 'easy', label: 'лёгкий' },
  { value: 'normal', label: 'средний' },
  { value: 'hard', label: 'сложный' },
]

/**
 * За одним устройством игроков несколько, а экран один: своего игрока нет
 * вовсе. Ни «ваш ход», ни разворот доски под кого-то одного тогда не имеют
 * смысла. Когда человек за столом один (партия с компьютером без сервера),
 * режим обычный.
 */
function passAround(view: GameView | undefined): boolean {
  return view?.local === true && view.players.filter((player) => player.bot === null).length > 1
}

type Props = {
  gameId: string
  /** Кто смотрит, если он вошёл в учётную запись. Нужен, чтобы звать друзей. */
  me?: UserCard | null
  /** Куда уйти, выйдя из партии. */
  onLeave?: () => void
  /** Открыть другую партию — следующую, начатую из финала. */
  onOpen?: (gameId: string) => void
  /**
   * Перейти по адресу приложения: правила и настройки открываются из
   * полосы партии, когда шапки на экране нет (телефон лёжа).
   */
  onNavigate?: (path: string) => void
}

export function GameScreen({ gameId, me = null, onLeave, onOpen, onNavigate }: Props) {
  const client = useQueryClient()
  const board = useBoard()
  const game = useGame(gameId)
  const online = useConnection(gameId)
  const [selected, setSelected] = useState<number | null>(null)
  const [name, setName] = useState(savedName)
  const [copied, setCopied] = useState(false)
  /*
   * Подтверждение выхода из начатой партии.
   *
   * Двумя нажатиями, а не окном браузера: окно блокирует страницу и
   * выглядит как ошибка, а бросить партию — решение, не авария. Первое
   * нажатие спрашивает, второе делает.
   */
  const [dropping, setDropping] = useState(false)
  /*
   * Приближение доски. Хук стоит здесь, до всех условных возвратов ниже
   * (лобби, ошибка, загрузка): порядок хуков обязан быть одним и тем же на
   * каждом рендере, а из лобби экран уходит в партию без размонтирования.
   */
  const { pan, zoom, reset, remember } = useBoardZoom()
  /*
   * Телефон в портрете — своя раскладка партии (usePhoneCamera): доска крупнее
   * экрана, камера, ход нажатием на клетку, куда идти. Цель под пальцем —
   * здесь: пока держат, у неё виден путь.
   */
  const camera = usePhoneCamera()
  const [pressed, setPressed] = useState<string | null>(null)
  /*
   * Доска развёрнута своим двором вниз.
   *
   * Считается ДО остальных хуков и один раз на всех: развёрнутую доску ждут и
   * показ ходов (usePlayback держит на ней фишки, до которых очередь не
   * дошла), и раскладка фишек, и отрисовка. Дай им разные — фишки прыгали бы
   * между двумя системами координат.
   *
   * За одним устройством не разворачиваем: своего игрока там нет, а поворот
   * на каждом ходу — отдельный разговор, и он не должен случаться посреди
   * анимации.
   */
  const facing = passAround(game.data) ? null : (game.data?.you?.color ?? null)
  const geometry = useMemo(
    () => (board.data === undefined ? undefined : faceTo(board.data, facing)),
    [board.data, facing],
  )
  // Блок результата: из него кубики вырастают в оверлей и туда же
  // возвращаются после броска. Пока они там, в панели их не показываем —
  // это одни и те же кубики.

  const view = game.data

  // Ход компьютера держится на джобе, и она может пропасть: если доска
  // замерла, просим сервер его разбудить.
  useBotWatchdog(gameId, view)

  /*
   * Броски и ходы показываются по очереди, в том порядке, в каком случились:
   * иначе фишка идёт по доске, пока кубики ещё крутятся, а следующий бросок
   * ложится поверх недоигранного хода.
   *
   * Стоит до чата: тому нужен cursor, чтобы придержать реплику бота, пока
   * доска не доиграла сцену, которую эта реплика называет (см. reveal ниже).
   */
  const {
    walking,
    roll: watched,
    rollShown,
    held,
    busy,
    hurry,
    seenRoll,
    actor,
    cursor,
  } = usePlayback(view, geometry, game.data?.you?.color ?? null)

  /*
   * Разговор за столом.
   *
   * Хук стоит здесь, до всех условных возвратов: порядок хуков обязан быть
   * одним и тем же на каждом рендере, а из лобби экран уходит в партию без
   * размонтирования.
   *
   * У партии за одним устройством чата нет вовсе: собеседник сидит рядом.
   * Выключенный в настройках — то же самое, только по своей воле; запрос
   * тогда не уходит.
   *
   * И то же самое у зрителя за открытым столом (hasChat): истории там не
   * показывается, а писать он не может — показывать ему нечего.
   */
  const chatOn = useChatOn()
  const chatting = chatOn && view !== undefined && view.local !== true && hasChat(view)
  const chat = useChat(gameId, chatting, view?.you?.seat ?? null)
  /*
   * Придержанные реплики: см. reveal в useChat.ts. Реплика бота ждёт, пока
   * доска не доиграет сцену, о которой она — иначе "шестёрка" звучала бы
   * раньше, чем кубики долетают до глаз.
   */
  const revealed = useMemo(() => reveal(chat.data, cursor), [chat.data, cursor])
  const messages = revealed ?? []
  // Пузырям отдаётся revealed как есть, не messages: им важно отличить «ещё
  // не загрузились» от «загрузились и пусто» — иначе весь хвост истории
  // всплыл бы пузырями.
  const speech = useSpeech(revealed, gameId, chatting)
  // Подбор бонуса — подписью у двора, в такт очереди показа.
  const callouts = useBonusCallouts(game.data?.feed, cursor)

  /*
   * Доигранную партию не предлагают продолжить.
   *
   * Отметка ставится здесь, а не на сервере: «продолжить» — это про браузер
   * игрока, список партий лежит в нём же. Узнаём об окончании ровно тогда,
   * когда партия открыта, — этого и достаточно: попасть в меню, не побывав
   * в партии, нельзя.
   */
  useEffect(() => {
    if (view?.status === 'finished' || view?.status === 'abandoned') markOver(gameId)
  }, [gameId, view?.status])

  /*
   * Партии по этому адресу нет — и запись о ней в браузере тоже не нужна.
   *
   * Её убрала уборка (брошенный стол живёт двадцать минут) или адрес набран
   * неверно. Запись при этом оставалась и всплывала кнопкой «Продолжить» на
   * главной: кнопка есть, а по нажатию «партия не найдена», и так каждый
   * раз. Забываем здесь, потому что только здесь про пропажу и известно.
   */
  const gone = isGone(game.error)

  useEffect(() => {
    if (gone) forgetGame(gameId)
  }, [gone, gameId])

  // Компьютер партии без сервера ходит здесь же — по таймеру и только когда
  // доска доиграла предыдущую сцену.
  useLocalBot(gameId, view, busy)

  // Ушедший со страницы незапущенного стола снимается с места.
  useLobbyPresence(gameId, view?.status === 'lobby', (view?.you ?? null) !== null)

  /*
   * Чей ход — по тому, что показывается, а не по тому, что уже записано
   * в партии. Сервер передаёт ход дальше сразу, а на доске в это время ещё
   * доигрывает предыдущий: подсветка чужого хода поверх своего сбивает.
   *
   * Считается здесь, до ранних возвратов, потому что от него зависит хук
   * звука. Заодно у «чей ход» остаётся одно определение на весь экран:
   * подсветка, строка и звук обязаны говорить одно и то же.
   */
  const showColor = actor ?? view?.currentColor ?? null
  /*
   * Звонок на переходе хода. За чей ход звонить — решает вид партии:
   *
   * в сетевой только за свой, потому что на чужом ходу делать по звонку
   * нечего, и вчетвером он превратился бы в шум;
   *
   * за одним устройством — за каждый: там смысл ровно в том, что телефон
   * пора передать, и передаче нужен слышимый знак.
   */
  useCueOnChange(
    'turn',
    showColor,
    view?.status === 'playing' &&
      (passAround(view) || (showColor !== null && showColor === (view?.you?.color ?? null))),
  )
  /*
   * Стука кубиков здесь нет намеренно: его подаёт CentreDice, в тот самый миг,
   * когда кубики легли. Экран знал только, что бросок стал текущей сценой, а
   * чужие кубики после этого ещё секунду лежат и кувыркаются — звук уходил
   * вперёд картинки. Подробности там же, у land().
   */
  /*
   * Начало и конец партии — на смене её состояния. Открытая уже начатой или
   * уже доигранной молчит: перехода в неё не показывали, и ни объявлять, ни
   * поздравлять не с чем. Обе метки — одно и то же состояние, поэтому и
   * зазвонить разом они не могут.
   *
   * Но состояние берётся ПОКАЗАННОЕ, а не записанное в партии. Сервер ставит
   * «доиграна» сразу, а на доске в это время последняя фишка ещё идёт домой:
   * поздравление звучало раньше, чем она доезжала, и раньше собственного
   * звука дома. Пока очередь показа не пуста, партия для звука всё ещё идёт.
   *
   * В лобби очередь пуста всегда — сцены есть только у начатой партии, —
   * поэтому подмена «занято → playing» начала не затрагивает.
   */
  const shownStatus = busy ? 'playing' : (view?.status ?? null)
  useCueOnChange('start', shownStatus, shownStatus === 'playing')
  // Финал — победителю и проигравшим разными голосами: см. finaleCue.
  useCueOnChange(finaleCue(view), shownStatus, shownStatus === 'finished')

  /*
   * Готовые звуки подхватываются на входе в партию: на лобби и правилах они
   * не нужны, а первый знак не должен отличаться от следующих.
   */
  useEffect(() => {
    warmSounds()
  }, [])

  const save = (data: GameView) => {
    client.setQueryData(gameKey(gameId), data)
    setSelected(null)
  }

  const join = useMutation({
    mutationFn: () => joinGame(gameId, me === null ? name : ''),
    onSuccess: save,
  })
  /*
   * Сесть за этот стол мешает прежняя партия: активная у человека одна
   * (GameController::oneAtATime). Адрес прежней сервер присылает в отказе —
   * по нему её и предлагается бросить, не уходя с приглашения.
   */
  const busyWithGame = busyWith(join.error)
  const dropOther = useMutation({
    mutationFn: async () => {
      if (busyWithGame !== null) await leaveGame(busyWithGame)
    },
    onSuccess: () => join.mutate(),
  })
  const move = useMutation({
    mutationFn: (steps: MoveStep[]) => sendMove(gameId, steps),
    onSuccess: save,
  })
  const roll = useMutation({ mutationFn: () => rollDice(gameId), onSuccess: save })
  const seatBot = useMutation({
    mutationFn: (chosen: BotLevel) => addBot(gameId, chosen),
    onSuccess: save,
  })
  const ready = useMutation({
    mutationFn: (yes: boolean) => setReady(gameId, yes),
    onSuccess: save,
  })
  const leave = useMutation({ mutationFn: () => leaveGame(gameId), onSuccess: () => onLeave?.() })
  const freeSeat = useMutation({
    mutationFn: (seat: number) => removeBot(gameId, seat),
    onSuccess: save,
  })

  // Пропавшая партия и потерянная связь — разные беды, и слова к ним разные:
  // первую переоткрывать бессмысленно, вторую — единственное, что и остаётся.
  if (gone) {
    return (
      <div className="card">
        <p>Такой партии больше нет. Брошенные столы убираются, чтобы не ждать за ними никого.</p>
        <a className="ghost" href="/">
          на главную
        </a>
      </div>
    )
  }

  if (board.isError || game.isError) {
    return <p className="card">не удалось загрузить партию — сервер недоступен</p>
  }

  if (geometry === undefined || view === undefined) {
    return <p className="card">загружаем партию…</p>
  }

  const mine = view.you?.color ?? null
  const iAmIn = view.you !== null
  const myTurn = view.status === 'playing' && mine !== null && view.currentColor === mine
  const myMoves = myTurn ? view.allowed.filter((candidate) => candidate.color === mine) : []

  // Пока партия не началась, доски нет: смотреть на пустое поле незачем,
  // а собраться и позвать соперников — есть зачем.
  if (view.status === 'lobby') {
    // Хозяин стола приходит с сервера: по имени это определялось неверно,
    // стоило за столом оказаться двум тёзкам.
    const iAmHost = view.you?.host === true
    const seats = Array.from({ length: 4 }, (_, seat) => view.players[seat])
    const full = view.players.length >= 4
    const meReady = view.players.find((player) => player.seat === view.you?.seat)?.ready === true
    const readyCount = view.players.filter((player) => player.ready).length

    return (
      <div className="lobby">
        <div className="hero">
          <h2 className="hero__title">Собираемся</h2>
          <p className="hero__lead">
            {view.visibility === 'public'
              ? 'Стол открыт: соперник может подсесть сам, пока вы ждёте. Ссылку тоже можно отправить.'
              : 'Отправьте соперникам ссылку — за этот стол сядут только те, кому вы её дали.'}{' '}
            Цвета раздаются при старте: вдвоём — по разные стороны доски, вчетвером — по всем углам.
          </p>
        </div>

        <div className="card">
          <h2 className="card__title">Ссылка на партию</h2>
          <div className="invite">
            <input readOnly value={window.location.href} onFocus={(e) => e.target.select()} />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(window.location.href)
                setCopied(true)
              }}
            >
              {copied ? 'скопировано' : 'копировать'}
            </button>
          </div>
        </div>

        <div className="card">
          <h2 className="card__title">За столом {view.players.length} из 4</h2>
          <div className="seats">
            {seats.map((player, seat) => (
              <div
                // Пустые места ничем друг от друга не отличаются, кроме
                // порядка, — ключом может быть только он.
                // biome-ignore lint/suspicious/noArrayIndexKey: см. выше
                key={`seat-${seat}`}
                className={`seat${player === undefined ? ' seat--empty' : ''}${
                  player?.ready === true ? ' seat--ready' : ''
                }`}
              >
                {player === undefined ? (
                  <>
                    {/* Пустой кружок вместо номера: место в списке узнаётся
                        по фишке игрока, а нумеровать стулья незачем — за
                        столом важно, кто сидит, а не какой он по счёту. */}
                    <span className="avatar avatar--empty" aria-hidden="true" />
                    <span className="seat__who">
                      <span className="player__name">свободно</span>
                    </span>
                  </>
                ) : (
                  <>
                    <Avatar name={player.name} src={player.avatar} bot={player.bot !== null} />
                    {/* Имя и метки в одну строку: они про одного и того же
                        человека, и разносить их по этажам не за чем. */}
                    <span className="seat__who">
                      <span className="player__name">{player.name}</span>
                      {/*
                        Готовность показана цветом плашки — подпись рядом
                        с именем спорила бы с меткой сложности. Слово всё же
                        есть, просто невидимое: цвет сам по себе читалке
                        ничего не говорит, а браузерная подсказка при
                        наведении только мешала.
                      */}
                      {player.ready === true && <span className="sr-only">готов</span>}
                      {view.you?.seat === player.seat && <span className="chip">вы</span>}
                      {/* Кто он такой, говорит значок на месте буквы,
                          поэтому метке остаётся только сложность. */}
                      {player.bot !== null && (
                        <span className="chip">
                          {LEVELS.find((one) => one.value === player.bot)?.label}
                        </span>
                      )}
                    </span>
                    {iAmHost && player.bot !== null && (
                      <button
                        type="button"
                        className="ghost seat__action"
                        aria-label={`убрать ${player.name}`}
                        onClick={() => freeSeat.mutate(player.seat)}
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Вошедшему имя вводить не надо: он подписан своим ником. */}
          {!iAmIn && (
            <form
              className="field"
              onSubmit={(event) => {
                event.preventDefault()
                if (me !== null || name.trim() !== '') join.mutate()
              }}
            >
              {me === null && (
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ваше имя"
                  maxLength={40}
                />
              )}
              <button className="primary" type="submit" disabled={join.isPending}>
                Войти в партию
              </button>
            </form>
          )}

          {busyWithGame !== null && (
            <div className="row">
              <span className="banner banner--warn">сначала надо закончить прежнюю партию</span>
              <a className="ghost" href={`/g/${busyWithGame}`}>
                вернуться в неё
              </a>
              <button
                className="ghost"
                type="button"
                disabled={dropOther.isPending}
                onClick={() => dropOther.mutate()}
              >
                бросить её и сесть здесь
              </button>
            </div>
          )}

          {/* Компьютер сажает хозяин стола — по одному и сразу нужного
              уровня: выбирать сложность отдельным переключателем не за чем,
              когда уровней всего три. */}
          {iAmHost && !full && (
            <div className="add-bot">
              <h3 className="add-bot__title">Добавить компьютер</h3>
              <div className="field field--split">
                {LEVELS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    disabled={seatBot.isPending}
                    onClick={() => seatBot.mutate(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Готовность вместо кнопки «начать»: партия начинается сама, когда
            готовы все за столом, и не зависит от одного человека. */}
        {iAmIn && (
          <div className="card ready">
            <button
              className={view.you?.seat !== undefined && meReady ? 'wide' : 'primary wide'}
              type="button"
              disabled={ready.isPending}
              onClick={() => ready.mutate(!meReady)}
            >
              {meReady ? 'Я передумал' : 'Я готов'}
            </button>
            <p className="muted">
              {view.players.length < 2
                ? 'Партия начнётся, когда за столом будет хотя бы двое и все нажмут «готов».'
                : `Ждём остальных: готовы ${readyCount} из ${view.players.length}.`}
            </p>
          </div>
        )}

        {/* Звать друзей может только вошедший: друзья есть у учётной
            записи, а не у места за столом. */}
        {iAmHost && me !== null && !full && (
          <FriendInvite
            gameId={gameId}
            seated={view.players
              .map((player) => player.userId)
              .filter((userId): userId is number => userId !== null)}
          />
        )}

        {/* В лобби чат — обычная карточка: шторка тут не нужна, места
            хватает, а разговор, пока собираются, — половина смысла лобби.
            За открытым столом в ней будет только набор фраз: истории там
            не показывается. */}
        {chatting && (
          <div className="card">
            <h2 className="card__title">За столом</h2>
            <ChatBox view={view} messages={messages} />
          </div>
        )}

        {/* Уйти со стола, пока партия не началась: брошенный стол сервер
            удаляет, а открытый иначе предлагался бы живым людям. */}
        {iAmIn && (
          <div className="row">
            <button
              className="ghost"
              type="button"
              disabled={leave.isPending}
              onClick={() => leave.mutate()}
            >
              {view.you?.host === true ? 'Убрать стол' : 'Выйти из партии'}
            </button>
            {leave.isError && <span className="muted">не удалось выйти</span>}
          </div>
        )}
      </div>
    )
  }

  const phone = camera.phone && view.status === 'playing'
  /*
   * Какая фишка выбрана. На телефоне, если сходить может только одна, она
   * выбирается сама: её кружки видны сразу, без лишнего касания. Кружки всех
   * фишек разом не показываем — в глазах от них рябило.
   */
  const movers = [...new Set(myMoves.map((one) => one.token))]
  const chosen = selected ?? (phone && movers.length === 1 ? movers[0] : null)

  const placed = placeTokens(geometry, view.tokens, {
    mine,
    moves: myMoves,
    selected: chosen,
    walking,
  })

  /*
   * Показ подменяет клетки фишек, до которых очередь ещё не дошла.
   *
   * В состоянии партии они уже переехали — сервер отдаёт итог, — и без
   * подмены фишка оказывалась бы в конце пути раньше, чем начинала по нему
   * идти: сначала прыжок, потом «анимация» того, что уже случилось. Ровно
   * это и выглядело как «компьютер ходит быстрее анимации». Срубленная
   * фишка по той же причине стоит на своей клетке, пока её не собьют
   * на глазах.
   */
  const tokens = placed.map((token) => {
    const stay = held.find(
      (candidate) => candidate.color === token.color && candidate.token === token.token,
    )

    return stay === undefined ? token : { ...token, cell: stay.cell }
  })

  /*
   * Кто за столом — для карточек во дворах. Считается счёт по двору: сколько
   * фишек игроку осталось завести в игру. Сколько у него дома, карточка не
   * показывает — это видно по его домашней дорожке.
   */
  const people: BoardPerson[] = view.players.flatMap((player) =>
    player.color === null
      ? []
      : [
          {
            color: player.color,
            name: player.name,
            avatar: player.avatar,
            frame: player.skins?.frame ?? null,
            waiting: (view.tokens?.[player.color] ?? []).filter(
              (position) => position.location === 'base',
            ).length,
          },
        ],
  )

  const options: MoveOption[] =
    mine === null ? [] : optionsOf(geometry, mine, myMoves, view.combos, chosen)

  /*
   * Пока ход наш и кубик не брошен, других действий на экране нет вовсе:
   * ни целей, ни выбора. Промахнуться нечем, поэтому мишенью для броска
   * становится вся доска, а не два кубика в центре. Приглашение остаётся
   * там же, где кубики (CentreDice): выпавшее видно там, где его бросали.
   */
  const asking = myTurn && view.turn?.awaiting === true && !busy

  const pickTarget = (key: string) => {
    const chosen = options.find((option) => option.key === key)

    if (chosen !== undefined) move.mutate(chosen.steps)
  }

  const pickToken = (token: number) => setSelected(token === chosen ? null : token)

  // Ход, в котором ходить было нечем: кубики сгорели и ход ушёл дальше.
  // Про это надо сказать словами — иначе партия, где сходить не может никто
  // (например, все ждут единицу, чтобы завести последнюю фишку в дом),
  // выглядит как зациклившийся бросок.
  // События бонусов не в счёт: после пропуска хода сервер может записать
  // появление набора, и пропуск перестал бы быть последним.
  const lastEvent = view.feed.findLast((event) => event.type !== 'bonus')
  const skipped = lastEvent?.type === 'skip' ? ((lastEvent.payload.color as string) ?? null) : null
  /*
   * Тот же случай, но ход не ушёл: выпавшим ходить нечем, а доброс за него
   * остался (docs/RULES.md, решение 1). Пропуска сервер не пишет — ход
   * никуда не уходит, — и признак здесь другой: последним в ленте лежит наш
   * бросок, а ход снова ждёт броска. Без объяснения это выглядит как бросок,
   * не давший ничего: чаще всего в конце партии, где последней фишке нужно
   * точное число, а выпадает шестёрка.
   */
  const burnedBonus =
    lastEvent?.type === 'roll' && lastEvent.payload.color === mine && view.turn?.awaiting === true
  const burned = (skipped !== null && skipped === mine) || burnedBonus
  /*
   * Погашенные кубики — только у СВЕЖЕГО броска.
   *
   * turn.dice — остаток текущего броска партии, а в центре может лежать
   * предыдущий: очередь показа отстаёт, и свой прошлый бросок остаётся
   * лежать, пока не бросили заново. Вычитать остаток из чужого броска —
   * значит гасить кубики случайным образом, поэтому у несвежего броска
   * пометок нет вовсе.
   */
  const fresh = seenRoll !== null && seenRoll.seq === lastRoll(view.feed)?.seq
  const diceUsed = fresh && seenRoll !== null ? usedFlags(seenRoll.dice, view.turn?.dice ?? []) : []
  const nameOf = (colour: string | null) =>
    view.players.find((player) => player.color === colour)?.name ?? '—'
  /*
   * Рельса показывает фишки ТОГО, ЧЕЙ ХОД, а не «мои»: за одним устройством
   * своего игрока нет вовсе. На чужом ходу она остаётся — чужими фишками не
   * сходить, но убрать её нельзя: доска центрируется по высоте и прыгала бы
   * на каждом ходу.
   */
  const railFor = showColor === null ? null : (view.tokens?.[showColor] ?? null)
  const tutor = view.status !== 'playing' ? null : (view.tutorial ?? null)
  const botTurn =
    view.players.find((player) => player.color === view.currentColor)?.bot != null
      ? view.currentColor
      : null
  const guides = guidesOf(tutor, myTurn && !busy, asking, mine, botTurn, chosen, options)
  const homes = Object.fromEntries(
    view.players.flatMap((player) =>
      player.color === null
        ? []
        : [
            [
              player.color,
              (view.tokens?.[player.color] ?? []).filter((token) => token.location === 'home')
                .length,
            ],
          ],
    ),
  )
  const focus = phone
    ? cameraFocus({
        seq: view.feed[view.feed.length - 1]?.seq ?? 0,
        tokens,
        myTurn,
        asking,
        options,
        mine,
        chosen,
      })
    : { cells: null, key: '' }

  // В чужой ход — одно и то же «Ходит …» весь ход: чередование с «ход идёт»
  // на каждой сцене мигало строкой и читалось как дрожь.
  const statusText = asking
    ? 'Ваш ход'
    : busy && (myTurn || showColor === mine)
      ? 'ход идёт'
      : myTurn && options.length > 0
        ? 'Куда идём?'
        : myTurn && myMoves.length > 0
          ? 'Выберите фишку'
          : myTurn
            ? 'Ваш ход'
            : `Ходит ${nameOf(showColor)}`

  const dice = (
    <CentreDice
      // Приглашение бросить ждёт своей очереди наравне с остальным:
      // сервер может передать ход нам, пока на доске ещё доигрывается
      // чужой, — и тогда сначала доигрывается он.
      active={myTurn && view.turn?.awaiting === true && !busy}
      count={view.turn?.rolled === true ? Math.min(view.turn.bonus, 2) : 2}
      shown={seenRoll}
      used={diceUsed}
      burned={burned}
      watch={watched}
      onWatched={rollShown}
      hurry={hurry}
      onRoll={() => roll.mutate()}
      // Кубики того, чей ход показан, — того же, чьим цветом
      // горит ободок стола.
      skin={view.players.find((player) => player.color === showColor)?.skins?.dice ?? null}
    />
  )

  return (
    <>
      <div className={`play${phone ? ' play--phone' : ''}`}>
        <div>
          {phone && (
            <>
              <PhonePlay />
              <CameraAim
                look={camera.look}
                focus={focus.cells}
                focusKey={focus.key}
                size={geometry.size}
              />
              <PlayersBar
                players={view.players}
                homes={homes}
                current={showColor}
                menu={[
                  { label: 'В меню', run: () => onLeave?.() },
                  { label: 'Как играть', run: () => onNavigate?.('/rules') },
                  { label: 'Настройки', run: () => onNavigate?.('/settings') },
                  ...(iAmIn ? [{ label: 'Бросить партию', run: () => setDropping(true) }] : []),
                ]}
              />
            </>
          )}
          {/*
            Доска и руки — одной обёрткой.
            
            В портрете они стоят друг под другом, в ландшафте на телефоне —
            рядом: высоты там нет вовсе (740×360 оставляет доске 120 пикселей,
            это пять пикселей на клетку), зато есть ширина.
          */}
          <div className="table">
            {/* Ободок стола — цветом того, чей сейчас ход. */}
            <div
              className={`board-wrap${phone ? ' board-wrap--camera' : zoom > 1 ? ' board-wrap--zoomed' : ''}`}
              style={own(showColor)}
            >
              {/*
              Панорама — прокруткой этого слоя, приближение — шириной слоя
              внутри. Кубики лежат ВНУТРИ зумируемого слоя: они стоят в центре
              доски и при приближении обязаны уезжать вместе с ней. Оттуда же
              считается cqw их размера — container-type стоит на .board-zoom,
              а не на обёртке.

              Финал — наоборот, снаружи: это итог партии, а не часть поля.
              Внутри окна прокрутки его резало по краям, а центрировался он
              по приближённой доске, а не по экрану.
            */}
              <div
                className={`board-pan${phone ? ' board-pan--camera' : zoom > 1 ? ' board-pan--zoomed' : ''}`}
                ref={phone ? camera.view : pan}
                onScroll={phone ? undefined : remember}
              >
                <div
                  className={`board-zoom${phone ? ' board-zoom--camera' : ''}`}
                  style={
                    phone
                      ? ({
                          '--board-base': `${camera.base}px`,
                          transform: camera.transform,
                        } as CSSProperties)
                      : ({ '--zoom': zoom } as CSSProperties)
                  }
                >
                  <BoardView
                    board={geometry}
                    tokens={tokens}
                    // На телефоне имена — в полосе игроков, а во дворах лежат
                    // сами ждущие фишки: без карточек доска спокойнее.
                    people={phone ? [] : people}
                    current={showColor}
                    // Реплики приходят по МЕСТУ за столом, а доска знает
                    // игроков по цвету: цвет появляется только со старта
                    // партии, поэтому связывает их стол, а не реплика.
                    speech={[
                      ...view.players.flatMap((player) => {
                        const said = speech.get(player.seat)
                        // Подпись о бонусе на пару секунд перебивает реплику:
                        // пузырь один на двор.
                        const busy = callouts.speech.some((one) => one.color === player.color)

                        return player.color === null || said === undefined || busy
                          ? []
                          : [{ color: player.color, text: said }]
                      }),
                      ...callouts.speech,
                    ]}
                    flashes={callouts.flashes}
                    guides={phone ? guides.filter((guide) => !('dice' in guide)) : guides}
                    guideScale={phone ? camera.scale : 1}
                    targets={options}
                    pressed={pressed}
                    onPressTarget={phone ? setPressed : undefined}
                    onPickToken={(picked) => pickToken(picked.token)}
                    onPickTarget={pickTarget}
                    onPickBackground={() => (asking ? roll.mutate() : setSelected(null))}
                    bonuses={view.bonuses?.items ?? []}
                    shields={view.bonuses?.shields ?? []}
                    emblems={Object.fromEntries(
                      view.players.flatMap((player) =>
                        player.color === null ? [] : [[player.color, player.skins?.token ?? null]],
                      ),
                    )}
                  />

                  {dice}
                </div>
              </div>

              {view.status === 'finished' && (
                <Finale view={view} again={againOf(view, me)} onStarted={(id) => onOpen?.(id)} />
              )}
              {view.status === 'abandoned' && <Abandoned />}

              {/*
              Выход из приближения. Двойным нажатием его не сделать: первый
              же тап по доске — это ход, и отменить его нельзя. Поэтому
              кнопка, и только когда приближение есть.
            */}
              {/* Вернуть общий план, когда камера приблизила доску. */}
              {phone && camera.zoomed && (
                <button className="board-reset" type="button" onClick={camera.overview}>
                  вся доска
                </button>
              )}
              {!phone && zoom > 1 && (
                <button className="board-reset" type="button" onClick={reset}>
                  вся доска
                </button>
              )}
            </div>

            {/*
            Рельса и полоса действия — под доской.
            
            Раньше на этом месте была лента игроков; имена уехали в дворы,
            и место освободилось под то, чего не было: выбор фишки без
            попадания по клетке и список ходов кнопками.
            
            Блок стоит на месте всю партию и не меняет высоты: доска
            центрируется по свободной высоте экрана, и стоило рельсе или
            полосе исчезнуть — на чужом ходу, на анимации хода, — как доска
            съезжала. Поэтому меняется только содержимое.
          */}
            {/*
              Нижняя полоса телефона: где камера на доске, что происходит,
              кубики. Рельсы и кнопок ходов нет — ход делается нажатием на
              клетку, куда идти.
            */}
            {/*
              Подсказка «бросьте кубики» на телефоне — карточкой над нижней
              полосой: кубики там, а не в центре доски, куда смотрела стрелка.
            */}
            {phone &&
              guides
                .filter((guide) => 'dice' in guide)
                .map((guide) => (
                  <div key={guide.title} className="phone-tip" role="status">
                    <b>{guide.title}</b>
                    {guide.text !== undefined && <span>{guide.text}</span>}
                  </div>
                ))}
            {phone && (
              <div className="phone-bar">
                <MiniMap board={geometry} tokens={tokens} frame={camera.frame} />
                <p className="phone-bar__status">{statusText}</p>
              </div>
            )}

            {view.status === 'playing' && !phone && (
              <div className="hands">
                {/*
                  Шапка вместо шапки — только у телефона лёжа (см. .game-bar
                  в ui.css): там шапка скрыта, чтобы доске досталась вся
                  высота экрана, а в меню, к правилам и к настройкам попасть
                  всё равно надо.
                */}
                <nav className="game-bar" aria-label="партия">
                  <button className="ghost" type="button" onClick={() => onLeave?.()}>
                    ← меню
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    aria-label="как играть"
                    onClick={() => onNavigate?.('/rules')}
                  >
                    <HelpIcon />
                  </button>
                  <button
                    className="ghost"
                    type="button"
                    aria-label="настройки"
                    onClick={() => onNavigate?.('/settings')}
                  >
                    <GearIcon />
                  </button>
                </nav>
                {railFor !== null && showColor !== null && (
                  <TokenRail
                    colour={showColor}
                    tokens={railFor}
                    own={myTurn && !busy}
                    movable={[...new Set(myMoves.map((move) => move.token))]}
                    selected={chosen}
                    onPick={pickToken}
                  />
                )}
                {options.length > 0 ? (
                  <MoveChips options={options} onPick={pickTarget} />
                ) : (
                  /*
                   * Одна полоса и на состояние, и на действие.
                   *
                   * Отдельной строки «чей ход» над доской больше нет: она
                   * говорила то же самое, только на другом конце экрана, и
                   * стоила 28 пикселей высоты. Чей ход видно ещё и по
                   * подсвеченному углу на доске.
                   */
                  <p className="act act--hint" style={own(showColor)}>
                    {busy ? (
                      'ход идёт'
                    ) : myTurn ? (
                      <>
                        {/*
                          За одним устройством «ваш ход» не говорит ничего:
                          телефон передают из рук в руки, и важно, ЧЕЙ он.
                          Имя отвечает всем.
                        */}
                        <b>{passAround(view) ? `Ходит ${nameOf(showColor)}` : 'Ваш ход'}</b>
                        {asking ? ' — тап по доске' : ' — выберите фишку'}
                      </>
                    ) : (
                      `Ходит ${nameOf(showColor)}`
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          {/*
            Чат — шторкой ПОВЕРХ доски, а не блоком под ней.
            
            Блок «руки» держит фиксированную высоту нарочно: доска
            центрируется по свободной высоте, и стоило бы чему-то под ней
            появиться или исчезнуть — доска съезжает. Поэтому шторка выведена
            из потока целиком (position: fixed в ui.css).
          */}
          {chatting && <ChatDock view={view} messages={messages} />}

          {!online && view.local !== true && (
            <p className="banner banner--warn">
              связь потеряна — ходы соперников появятся, когда она восстановится
            </p>
          )}
          {move.isError && (
            <p className="banner banner--error">ход не принят — обновите страницу</p>
          )}

          {/*
            Уйти из начатой партии.
            
            Раньше выйти было нельзя вовсе, и это оказалось хуже, чем
            «выход посреди игры — это сдача»: партия оставалась идти, место
            в ней было занято, а начать новую человек не мог — активная
            партия у него одна. Доиграть с соперником, который ушёл, всё
            равно нельзя, а брошенную партию хотя бы убирает уборка.
          */}
          {view.status === 'playing' && iAmIn && (!phone || dropping) && (
            <div className="row drop-row">
              {dropping ? (
                <>
                  <span className="muted">бросить партию? вернуться в неё не получится</span>
                  <button
                    className="ghost"
                    type="button"
                    disabled={leave.isPending}
                    onClick={() => leave.mutate()}
                  >
                    да, бросить
                  </button>
                  <button className="ghost" type="button" onClick={() => setDropping(false)}>
                    нет
                  </button>
                </>
              ) : (
                <button className="ghost" type="button" onClick={() => setDropping(true)}>
                  бросить партию
                </button>
              )}
              {leave.isError && <span className="muted">не удалось выйти</span>}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * Подсказки обучения: что нажать прямо сейчас — и зачем.
 *
 * Сначала кубики, потом фишка, потом клетка: подсказка идёт за игроком шаг
 * за шагом, а не показывает весь ход сразу — две стрелки на доске читались
 * бы как два разных хода. Пока доска доигрывает сцену, подсказки нет:
 * нажимать нечего, а текст про следующий шаг обгонял бы показ. Пока ходит
 * соперник, подсказка стоит у его двора — чтобы смотрели туда.
 */
function guidesOf(
  tutor: GameView['tutorial'] | null,
  acting: boolean,
  asking: boolean,
  mine: PlayerColor | null,
  opponent: PlayerColor | null,
  selected: number | null,
  options: MoveOption[],
): BoardGuide[] {
  if (tutor == null || tutor.done) return []

  const about = { text: tutor.text }

  if (opponent !== null) {
    return [{ yard: opponent, title: 'Ходит соперник', quiet: true, ...about }]
  }

  if (!acting || mine === null) return []

  if (asking) return tutor.roll === null ? [] : [{ dice: true, title: cap(tutor.roll), ...about }]

  const chosen = tutor.pointers.find((pointer) => pointer.token === selected)

  if (chosen !== undefined) {
    // Учат ходу на сумму — стрелка на его клетку, а не на промежуточную:
    // одиночный шаг тоже разрешён, но показывать две цели значит путать.
    const sums = options.filter((option) => option.steps.length > 1)

    return (sums.length > 0 ? sums : options).map((option) => ({
      cell: option.cell,
      title: cap(chosen.aim),
      ...about,
    }))
  }

  return tutor.pointers.map((pointer) => ({
    color: mine,
    token: pointer.token,
    title: cap(pointer.tip),
    ...about,
  }))
}

/**
 * Куда смотреть камере телефона — и ключ ситуации.
 *
 * Приближение — только когда фишка выбрана: она сама и её кружки. Область
 * маленькая, и наезд получается заметным. Бросок, выбор фишки и ход
 * соперника — на общем плане: кубики лежат в центре доски, и обстановку
 * должно быть видно.
 */
function cameraFocus({
  seq,
  tokens,
  myTurn,
  asking,
  options,
  mine,
  chosen,
}: {
  seq: number
  tokens: PlacedToken[]
  myTurn: boolean
  asking: boolean
  options: MoveOption[]
  mine: PlayerColor | null
  chosen: number | null
}): { cells: Cell[] | null; key: string } {
  if (myTurn && chosen !== null && options.length > 0) {
    const token = tokens.find((one) => one.color === mine && one.token === chosen)

    return {
      cells: [
        ...options.map((option) => option.cell),
        ...(token === undefined || token.waiting ? [] : [token.cell]),
      ],
      key: `options-${seq}-${chosen}`,
    }
  }

  // Бросок — на общем плане: кубики лежат в центре доски, и перед ходом
  // видна вся обстановка.
  if (myTurn && asking) return { cells: null, key: `roll-${seq}` }

  return { cells: null, key: 'overview' }
}

/** Заголовок подсказки — с прописной: в сценарии подписи записаны строчными. */
function cap(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Финальный голос: фанфара победителю, её зеркало вниз — проигравшим.
 *
 * Слова финала уже различались («Вы победили!» / «Победил Миша»), а звук
 * стоял один на всех — и проигравшему играла чужая фанфара.
 *
 * Зритель (you === null) слышит фанфару: она про саму победу, а не про его
 * счёт. За одним устройством победивший человек сидит на этом экране сам
 * (seatOfViewer в features/local), а если выиграл компьютер — проиграли
 * все за устройством, и им положено зеркало.
 */
export function finaleCue(view: GameView | undefined): 'win' | 'lose' {
  const you = view?.you

  if (view?.winnerColor == null || you == null || you.color === null) return 'win'

  return you.color === view.winnerColor ? 'win' : 'lose'
}

/** Следующая партия из финала: что написать на кнопке и как её начать. */
type Again = { label: string; start: () => Promise<string> }

/**
 * «Ещё партия» — с теми же соперниками и правилами, одним нажатием.
 *
 * Раньше финал уводил в меню, и следующая партия начиналась с тех же
 * вопросов, на которые человек ответил пять минут назад. Повторить можно
 * то, что собирается без людей: компьютер и игру за одним устройством.
 * Партию с живыми соперниками так не повторить — они могли уже уйти, —
 * поэтому там остаётся «Новая партия» в меню. После обучения — партия с
 * компьютером по сохранённым настройкам: ради неё обучение и проходили.
 */
function againOf(view: GameView, me: UserCard | null): Again | null {
  const bots = view.players.filter((player) => player.bot !== null)
  const humans = view.players.length - bots.length
  const variant = view.variant ?? 'classic'
  const name = me === null ? savedName() : ''

  if (view.tutorial != null) {
    return { label: 'Играть с компьютером', start: () => startWithBots(name, loadPrefs()) }
  }

  if (view.local === true) {
    const level = bots[0]?.bot ?? null

    return {
      label: 'Ещё партия',
      start: async () => createLocalGame(view.players.length, level, variant),
    }
  }

  if (humans === 1 && bots.length > 0) {
    const seats = Math.min(Math.max(view.players.length, 2), 4) as Prefs['seats']
    const level = bots[0].bot ?? 'normal'

    return { label: 'Ещё партия', start: () => startWithBots(name, { seats, level, variant }) }
  }

  return null
}

/**
 * Финал — поверх доски.
 *
 * Партия кончилась, и доска больше ни для чего не нужна: она превращается
 * в фон для итога. Слова разные: победителю — про него, остальным — про
 * того, кто выиграл. «Победил Гость» на своём экране читается обидно
 * ровно тем, что написано это про чужую победу теми же словами, что и про
 * свою.
 */
function Finale({
  view,
  again,
  onStarted,
}: {
  view: GameView
  again: Again | null
  onStarted: (gameId: string) => void
}) {
  const next = useMutation({
    mutationFn: async () => (again === null ? '' : again.start()),
    onSuccess: (id) => {
      if (id !== '') onStarted(id)
    },
  })
  const winner = view.players.find((player) => player.color === view.winnerColor)
  const iWon = view.you?.color !== undefined && view.you.color === view.winnerColor
  const standings = [...view.players].sort((a, b) => homeCount(view, b) - homeCount(view, a))

  return (
    <div className="finale" style={own(view.winnerColor)}>
      {/* Конфетти — единственное место, где игра позволяет себе праздник.
          Чисто на CSS: десяток кружков, разлетающихся от центра. */}
      {iWon && (
        <div className="confetti" aria-hidden="true">
          {Array.from({ length: 14 }, (_, index) => (
            // Кружки конфетти ничем не отличаются друг от друга, кроме
            // места в разлёте, — другого ключа у них нет.
            // biome-ignore lint/suspicious/noArrayIndexKey: см. выше
            <span key={`confetti-${index}`} style={{ '--i': index } as CSSProperties} />
          ))}
        </div>
      )}

      <div className="finale__card">
        <div className="finale__crown">{iWon ? '🏆' : '🎲'}</div>
        <p className="finale__who">{iWon ? 'Вы победили!' : `Победил ${winner?.name ?? '—'}`}</p>
        <p className="muted">
          {/* Обучение кончается победой всегда: сказать стоит о нём, а не о доме. */}
          {view.tutorial != null
            ? 'обучение пройдено — теперь можно играть по-настоящему'
            : iWon
              ? 'дом занят целиком'
              : `${COLOUR_NAME[view.winnerColor ?? ''] ?? ''} — дом целиком`}
        </p>

        <ol className="standings">
          {standings.map((player) => (
            <li key={player.seat} className="player">
              <span
                className={`player__dot${player.color === null ? '' : ` dot-${player.color}`}`}
              />
              <span className="player__name">{player.name}</span>
              <span className="player__counts">{homeCount(view, player)} из 5 дома</span>
            </li>
          ))}
        </ol>

        {again === null ? (
          <a className="finale__action" href="/">
            Новая партия
          </a>
        ) : (
          <>
            <button
              className="finale__action"
              type="button"
              disabled={next.isPending}
              onClick={() => next.mutate()}
            >
              {again.label}
            </button>
            {/* В меню — вторым, тихим действием: чаще всего после партии
                хотят ещё одну такую же, а не выбирать заново. */}
            <a className="finale__menu" href="/">
              в меню
            </a>
            {next.isError && <p className="muted">не удалось начать — попробуйте из меню</p>}
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Брошенная партия — поверх доски, как и финал.
 *
 * Отдельно от финала, потому что это не итог: победителя нет, и поздравлять
 * не с чем. Но и просто исчезнуть партия не может — соперник за тем же
 * столом должен прочитать, что случилось, а не получить пустую доску или
 * «партия не найдена».
 */
function Abandoned() {
  return (
    <div className="finale">
      <div className="finale__card">
        <div className="finale__crown">🚪</div>
        <p className="finale__who">Партия брошена</p>
        <p className="muted">кто-то вышел из-за стола, и доигрывать её не с кем</p>

        <a className="finale__action" href="/">
          Новая партия
        </a>
      </div>
    </div>
  )
}

function homeCount(view: GameView, player: { color: string | null }): number {
  if (player.color === null) return 0

  return (view.tokens?.[player.color] ?? []).filter((token) => token.location === 'home').length
}
